import { gsap } from 'gsap'

export default {
  data() {
    return {
      factsEntranceObserver: null,
      factsEntranceTween: null,
      factsEntranceAnimated: false,
    }
  },
  mounted() {
    window.addEventListener('vistat:prepare-export', this.finishFactsEntranceAnimationForExport)
    this.$nextTick(this.setupFactsEntranceAnimation)
  },
  beforeDestroy() {
    window.removeEventListener('vistat:prepare-export', this.finishFactsEntranceAnimationForExport)
    this.cleanupFactsEntranceAnimation()
  },
  watch: {
    items() {
      this.$nextTick(() => {
        this.cleanupFactsEntranceAnimation()
        this.factsEntranceAnimated = false
        this.setupFactsEntranceAnimation()
      })
    },
  },
  methods: {
    isFactsExportTarget(event) {
      const element = event?.detail?.element
      return !!(element && this.$el && (this.$el === element || this.$el.contains(element) || element.contains(this.$el)))
    },
    finishFactsEntranceAnimationForExport(event) {
      if (!this.isFactsExportTarget(event)) return

      const items = this.getFactsEntranceItems()
      const opacityTargets = items.map((item) => item.opacityTarget)
      const motionTargets = items.flatMap((item) => item.motionTargets)

      this.factsEntranceObserver?.disconnect()
      this.factsEntranceObserver = null
      if (this.factsEntranceTween) {
        this.factsEntranceTween.kill()
        this.factsEntranceTween = null
      }
      gsap.killTweensOf([...opacityTargets, ...motionTargets])
      gsap.set(opacityTargets, { clearProps: 'opacity' })
      gsap.set(motionTargets, { clearProps: 'transform,opacity' })
      this.factsEntranceAnimated = true
    },
    getFactsEntranceItems() {
      if (!this.$el?.querySelectorAll) return []

      return [
        ...[...this.$el.querySelectorAll('.facts-wrapper > .fact:not(.fake)')].map((item) => ({
          opacityTarget: item,
          motionTargets: [...item.children].filter(Boolean),
        })),
        ...[
          ...this.$el.querySelectorAll('.facts-color__item'),
          ...this.$el.querySelectorAll('.facts-minimalism__label'),
          ...this.$el.querySelectorAll('.facts-minimalism__item'),
        ].map((item) => ({
          opacityTarget: item,
          motionTargets: [item],
        })),
      ].filter((item) => item.opacityTarget && item.motionTargets.length)
    },
    getFactsEntranceTargets() {
      return this.getFactsEntranceItems()
        .flatMap((item) => [item.opacityTarget, ...item.motionTargets])
        .filter(Boolean)
    },
    setupFactsEntranceAnimation() {
      if (!process.client || this.factsEntranceAnimated) return

      const items = this.getFactsEntranceItems()
      if (!items.length) return

      const opacityTargets = items.map((item) => item.opacityTarget)
      const motionTargets = items.flatMap((item) => item.motionTargets)

      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
      if (prefersReducedMotion) {
        gsap.set(opacityTargets, { clearProps: 'opacity' })
        gsap.set(motionTargets, { clearProps: 'transform' })
        this.factsEntranceAnimated = true
        return
      }

      const isCompact = window.innerWidth < 720
      gsap.killTweensOf([...opacityTargets, ...motionTargets])
      gsap.set(opacityTargets, { opacity: 0 })
      items.forEach((item, index) => {
        gsap.set(item.motionTargets, {
          opacity: 0,
          y: isCompact ? 24 : 16,
          x: isCompact ? 0 : (index % 2 === 0 ? -28 : 28),
          scale: 0.96,
          transformOrigin: '50% 60%',
        })
      })

      const animate = () => {
        if (this.factsEntranceAnimated) return
        this.factsEntranceAnimated = true
        const timeline = gsap.timeline()

        items.forEach((item, index) => {
          const position = index * 0.09
          timeline
            .to(item.opacityTarget, {
              opacity: 1,
              duration: 0.96,
              ease: 'power2.out',
              clearProps: 'opacity',
            }, position)
            .to(item.motionTargets, {
              opacity: 1,
              x: 0,
              y: 0,
              scale: 1,
              duration: 1.38,
              ease: 'power3.out',
              clearProps: 'transform,opacity',
            }, position)
        })

        this.factsEntranceTween = timeline
      }

      if ('IntersectionObserver' in window) {
        this.factsEntranceObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.factsEntranceObserver?.disconnect()
            this.factsEntranceObserver = null
            animate()
          }
        }, {
          threshold: 0.16,
          rootMargin: '0px 0px -12% 0px',
        })
        this.factsEntranceObserver.observe(this.$el)
      } else {
        animate()
      }
    },
    cleanupFactsEntranceAnimation() {
      this.factsEntranceObserver?.disconnect()
      this.factsEntranceObserver = null
      if (this.factsEntranceTween) {
        this.factsEntranceTween.kill()
        this.factsEntranceTween = null
      }
      gsap.killTweensOf(this.getFactsEntranceTargets())
    },
  },
}
